import axios from "axios";

/**
 * Per-quote payment terms (staff): require a deposit, and/or switch the
 * early-payment incentive off. A required deposit always switches it off.
 */
export const setQuotePaymentTerms = async (
	id: string,
	terms: { requiresDeposit: boolean; depositPercentage?: number | null; earlyPaymentDiscountOff: boolean; hideClientPricingNote?: boolean },
): Promise<any> => {
	try {
		const res: any = await axios.put(`/quotes/${id}/payment-terms`, terms);
		return res?.data ?? { error: "No data returned." };
	} catch (e: any) {
		return { error: e?.response?.data?.message || "Saving payment terms failed." };
	}
};

export const getQuotes = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get("/quotes", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getQuote = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/quotes/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const emailQuote = async ({ data }: any) => {
	try {
		// Session 28 hotfix 6 (Phase 3): backend reads
		// quote.mulchMapSnapshotIds directly. No opt-in flag anymore.
		// WO_SEND_QUOTE.md (Session 54): optional operator-chosen recipient
		// + CC pass through when provided (the WO "Send quote" email
		// branch). Omitted -> backend uses today's primaryClient default.
		const payload: any = {};
		if (data?.recipientEmail != null) payload.recipientEmail = data.recipientEmail;
		if (Array.isArray(data?.cc)) payload.cc = data.cc;
		const res: any = await axios.post(`/quotes/${data.id}/pdf-by-email`, payload);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateQuote = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/quotes/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getQuotePDF = async ({ data }: any) => {	
	try {
		// Session 28 hotfix 6 (Phase 3): backend reads
		// quote.mulchMapSnapshotIds directly.
		const res: any = await axios.get(`/quotes/${data?.id}/pdf`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const acceptOrDeny = async ({ data, update }: any) => {
	try {
		const res: any = await axios.post(`/quotes/${data.id}/accept-or-deny`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Maintenance Module Phase 1 §3.9 #8 (open decision #10). Staff-only
// action for Corrigo-managed clients — records that the FM approved
// externally in CorrigoPro. externalWorkOrderNumber is required;
// approvalNote is optional.
export const acceptOnBehalf = async ({
	data,
	externalWorkOrderNumber,
	approvalNote,
}: {
	data: { id: string };
	externalWorkOrderNumber: string;
	approvalNote?: string;
}) => {
	try {
		const res: any = await axios.post(`/quotes/${data.id}/accept-on-behalf`, {
			externalWorkOrderNumber,
			approvalNote,
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Maintenance Module Phase 1 §3.9 #17 (Session 39) — line-by-line
// Corrigo paste-export data. Staff-only, read-only. See
// js_crm_backend src/modules/corrigo-export/services/corrigo-export.service.ts
// for the category-mapping assumptions.
export const getCorrigoExport = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/quotes/${data?.id}/corrigo-export`);
		return res?.data ? res.data : { quoteId: data?.id, lines: [], planId: null, planName: null };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { quoteId: data?.id, lines: [], planId: null, planName: null };
	}
};

// mockup 34 (Corrigo quote "Mark posted") — audit-only write that lights
// the quote's Posted(=Sent) rung on a work-order-fed plan. Mirrors the
// invoice-side markPostedToCorrigo (api/Invoices). Distinct from
// accept-on-behalf: posting is the earlier "pasted into Corrigo, awaiting
// FM approval" moment. See
// js_crm_backend src/modules/corrigo-export/services/corrigo-export.service.ts
export const markPostedToCorrigo = async ({ data, note }: { data: { id: string }; note?: string }) => {
	try {
		const res: any = await axios.post(`/quotes/${data.id}/corrigo-export/mark-posted`, { note });
		return res?.data ? res.data : { success: false };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false };
	}
};

// Session 65 item 8 - read/save the quote Corrigo panel's pre-posting
// checklist progress so it survives a refresh. Mirrors api/Invoices.
export const getCorrigoProgress = async ({ data }: { data: { id: string } }) => {
	try {
		const res: any = await axios.get(`/quotes/${data?.id}/corrigo-export/progress`);
		return res?.data ? res.data : { success: false, progress: null };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false, progress: null };
	}
};

export const saveCorrigoProgress = async ({ data }: { data: { id: string; progress: any } }) => {
	try {
		const res: any = await axios.patch(`/quotes/${data.id}/corrigo-export/progress`, { progress: data.progress });
		return res?.data ? res.data : { success: false };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false };
	}
};

export const hideDiscountPopup = async (quoteId: string) => {
	try {
		const res: any = await axios.put(`/quotes/${quoteId}/hide-discount-popup`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const applyDiscount = async (quoteId: string) => {
	try {
		const res: any = await axios.post(`/quotes/${quoteId}/apply-discount`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Quote-version history for a project, including superseded + soft-deleted
// versions. Staff-only endpoint (backend gates isCompany). Safe-empty on
// error so the history panel just renders nothing rather than breaking the
// surface it sits on.
export const getProjectQuoteHistory = async ({ projectId }: { projectId: string }) => {
	try {
		const res: any = await axios.get(`/quotes/by-project/${projectId}/history`);
		return res?.data?.items ? res.data.items : [];
	} catch (e: any) {
		return [];
	}
};
